<template>
  <section
    v-if="articles.length"
    class="articles"
  >
    <div :class="articlesInnerClass">
      <AppLink
        :to="`/journal/country/articles/${categoryStore.countrySlug}/`"
        target="_blank"
        class="articles__title"
      >
        Полезные
        <span
          >статьи<IconBase
            class="articles__title-icon"
            name="right-arrow"
            color="--alias-color-icon-subtle"
        /></span>
      </AppLink>

      <div class="articles__grid">
        <ArticleComponent
          v-for="(item, index) in slicedList"
          :key="index"
          :article="item"
          @article-click="useArticle"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { AppLink } from '@experience/core-app-link-lib';
  import { computed, onBeforeMount, ref } from 'vue';

  import { trackEvent } from '@/services/analytics';

  import { getArticlesList, type ArticleListItem } from '@/shared/api/articles';
  import { useCategoryStore } from '@/shared/store';
  import { IconBase } from '@/shared/ui/icon';

  import { type ArticleSnack } from '@/pages/seoCategoryListing/interface';

  import ArticleComponent from './articleComponent.vue';
  import { EExperienceFilterType } from '@/widgets/filtersBlock';
  import { useLogger } from '@/shared/utils/logger';

  const logger = useLogger();

  const categoryStore = useCategoryStore();

  const mapArticleToSnack = (item: ArticleListItem): ArticleSnack => ({
    createdAt: item.created_at instanceof Date ? item.created_at.toISOString() : String(item.created_at),
    geo: item.geo,
    id: item.id,
    intro: item.intro,
    lightboxPhotoUrl: item.main_photo.lightbox,
    publishedAt: item.published_at instanceof Date ? item.published_at.toISOString() : String(item.published_at),
    readingTime: item.reading_time,
    slug: item.slug,
    tagline: item.tagline,
    tags: [],
    thumbPhotoUrl: item.main_photo.thumbnail,
    title: item.title,
    type: item.type,
    updatedAt: item.updated_at instanceof Date ? item.updated_at.toISOString() : String(item.updated_at),
    url: item.url,
  });

  const articles = ref<ArticleSnack[]>([]);
  const slicedList = computed(() => (articles.value.length ? articles.value.slice(0, 4) : []));

  const useArticle = (article: ArticleSnack) => {
    const city = article.geo?.city?.[0];
    const country = article.geo?.country?.[0];

    let params = {
      article_id: article.id,
      article_name: article.title,
      category_title: categoryStore.seoCategoryTitle,
      category_id: categoryStore.seoCategoryId,
      city_id: city?.id,
      city_name: city?.name,
      country_id: country?.id,
      country_name: country?.name,
    };

    if (city) {
      params = { ...params, city_id: city?.id, city_name: city?.name };
    }

    if (country) {
      params = { ...params, country_id: country?.id, country_name: country?.name };
    }

    trackEvent('category:articles:article.click', params);
  };

  const articlesInnerClass = computed(() => {
    const { length } = slicedList.value;

    return `articles__inner articles__inner--${length <= 4 ? length : 4}`;
  });

  const loadArticles = async () => {
    try {
      const { data } = await getArticlesList({
        query: {
          city_id: categoryStore.filters[EExperienceFilterType.SEO_DEFAULT]?.city,
          page_size: 4,
          type: 'article',
        },
      });

      if (!data) {
        return;
      }

      articles.value = data.results.map(mapArticleToSnack);
    } catch (e) {
      logger.error(e);
      // pass
    }
  };

  onBeforeMount(loadArticles);
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography';
  @use '@experience/shared/dist/assets/adaptivity';

  .articles {
    margin: var(--alias-space-80) 0;

    &:last-child {
      padding-bottom: 0;
    }

    :deep {
      .articles__inner {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 24px;

        .articles__item-title {
          span {
            &:first-of-type {
              word-break: break-word;

              @include adaptivity.mobile {
                word-break: inherit;
              }
            }
          }
        }

        &.articles__inner--1 {
          .articles__item {
            grid-template-columns: repeat(9, 1fr);

            .articles__item-title {
              grid-column: 1 / 4;
            }

            .articles__item-photo {
              grid-column: 4 / 10;

              img {
                aspect-ratio: 16/10;
              }
            }
          }

          @media (max-width: 1024px) {
            .articles__item {
              grid-template-columns: repeat(12, 1fr);

              .articles__item-title {
                grid-column: 9 / 13;
              }

              .articles__item-photo {
                grid-row: 1;
                grid-column: 1 / 9;
              }
            }
          }
        }

        &.articles__inner--2 {
          .articles__grid {
            grid-template-columns: repeat(8, 1fr);
            grid-column: 5 / 13;
            gap: 24px;

            @include adaptivity.tablet {
              grid-column: 1 / -1;
            }
          }

          .articles__item {
            display: flex;
            flex-direction: column-reverse;
            gap: 16px;
            justify-content: flex-end;

            &:first-of-type {
              grid-column: 1 / 5;
            }

            &:last-of-type {
              grid-column: 5 / 9;
            }

            .articles__item-photo img {
              aspect-ratio: 1/1;
            }
          }
        }

        &.articles__inner--3 {
          .articles__grid {
            grid-template:
              [row1-start] 'first first first' [row1-end]
              [row2-start] '. second third' [row2-end]
              / 1fr 1fr 1fr;
            gap: 24px;
          }

          .articles__item {
            &:first-of-type {
              grid-area: first;
              grid-template-columns: repeat(3, 1fr);

              .articles__item-title {
                grid-column: 1 / 2;
              }

              .articles__item-photo {
                grid-column: 2 / 4;
                width: 100%;

                img {
                  aspect-ratio: 1.81;
                }
              }
            }

            &:not(:first-of-type) {
              display: flex;
              flex-direction: row-reverse;
              gap: 16px;

              .articles__item-title {
                span {
                  &:first-of-type {
                    @include typography.base-500-400-regular;

                    @include adaptivity.tablet {
                      @include typography.base-400-400-regular;
                    }
                  }

                  &:last-of-type {
                    display: none;
                  }
                }
              }

              .articles__item-photo {
                width: 70px;
                min-width: 70px;
                max-width: 70px;
                height: 70px;
                min-height: 70px;
                max-height: 70px;

                img {
                  aspect-ratio: 1/1;
                }
              }
            }

            &:nth-of-type(2) {
              grid-area: second;
            }

            &:nth-of-type(3) {
              grid-area: third;
            }
          }

          @include adaptivity.tablet {
            .articles__grid {
              grid-template:
                [row1-start] 'first first first' [row1-end]
                [row2-start] 'second third .' [row2-end]
                / 1fr 1fr 1fr;
            }

            .articles__item {
              &:first-of-type {
                .articles__item-title {
                  grid-column: 3 / 4;
                }

                .articles__item-photo {
                  grid-row: 1;
                  grid-column: 1 / 3;
                }
              }
            }
          }
        }

        &.articles__inner--4 {
          .articles__grid {
            grid-template:
              [row1-start] 'first first first' [row1-end]
              [row2-start] 'second third fourth' [row2-end]
              / 1fr 1fr 1fr;
          }

          .articles__item:nth-of-type(4) {
            grid-area: fourth;
            justify-content: flex-end;
          }

          @extend .articles__inner--3;

          @include adaptivity.tablet {
            .articles__grid {
              grid-template:
                [row1-start] 'first first first' [row1-end]
                [row2-start] 'second third fourth' [row2-end]
                / 1fr 1fr 1fr;
            }
          }
        }

        @include adaptivity.tablet {
          grid-template-rows: min-content 1fr;
          grid-gap: 16px 24px;
        }
      }
    }

    .articles__title {
      grid-column: 1 / 3;
      width: fit-content;
      height: fit-content;
      color: var(--alias-color-text-default);
      text-decoration: none;
      cursor: pointer;

      span {
        @include typography.base-800-750-extra;
      }

      &-icon {
        width: 20px;
        height: 20px;
      }

      @include typography.base-800-750-extra;

      @include adaptivity.tablet {
        grid-row: 1 / 2;
        grid-column: 1 / -1;

        span {
          display: inline-flex;
          align-items: center;

          @include typography.base-700-500-extra;
        }

        .icon {
          transform: translateY(2px);
        }

        @include typography.base-700-500-extra;
      }
    }

    .articles__grid {
      display: grid;
      grid-column: 4 / 13;
      justify-items: start;

      @include adaptivity.tablet {
        grid-row: 2 / 3;
        grid-column: 1 / -1;
      }

      @include adaptivity.mobile {
        display: flex;
        flex-direction: column;
      }
    }

    @include adaptivity.tablet {
      margin: var(--alias-space-24) 0 var(--alias-space-40);
    }
  }
</style>
